import { useState, useCallback } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { DdqSessionQuestion } from '@/types/api.types';
import { useSessionStore } from '@/stores/useSessionStore';
import { TierBadge } from './TierBadge';
import { SourceBadge } from './SourceBadge';
import { CitationPanel } from './CitationPanel';
import { ActionButtons } from './ActionButtons';
import { ResponseEditor } from './ResponseEditor';
import { VaultCapturePrompt } from './VaultCapturePrompt';
import { getTierColors } from '../utils/tierColors';

export interface QuestionCardProps {
  question: DdqSessionQuestion;
  isExpanded: boolean;
  isSelected: boolean;
  isEditing: boolean;
  editingResponse: string;
  onToggleExpand: () => void;
  onToggleSelect: () => void;
  onAccept: () => void;
  onReject: () => void;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onUpdateEditingResponse: (response: string) => void;
}

export function QuestionCard({
  question,
  isExpanded,
  isSelected,
  isEditing,
  editingResponse,
  onToggleExpand,
  onToggleSelect,
  onAccept,
  onReject,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onUpdateEditingResponse,
}: QuestionCardProps) {
  const currentSessionId = useSessionStore((s) => s.currentSessionId);
  const tierColors = getTierColors(question.response_tier);
  const userAction = question.userAction || 'Pending';
  const [vaultCaptured, setVaultCaptured] = useState(false);

  const showVaultCapture =
    userAction === 'Accepted' &&
    question.answer_source !== 'vault_data' &&
    question.answer_source !== 'firm_required' &&
    !vaultCaptured;

  const handleVaultSave = useCallback(() => {
    setVaultCaptured(true);
  }, []);

  const handleVaultSkip = useCallback(() => {
    setVaultCaptured(true);
  }, []);

  const handleHeaderClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.checkbox-area')) {
      return;
    }
    onToggleExpand();
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    onToggleSelect();
  };

  const rawResponseText = question.userResponse || question.suggested_response;
  const responseText = rawResponseText?.replace(/^Response:\s*/i, '');

  // Strip markdown syntax for clean collapsed preview text
  const previewText = responseText
    ?.replace(/\*\*(.*?)\*\*/g, '$1')   // **bold** → bold
    .replace(/\*(.*?)\*/g, '$1')         // *italic* → italic
    .replace(/#{1,6}\s+/g, '')           // ## headings → text
    .replace(/\|/g, ' ')                 // | table pipes → spaces
    .replace(/-{3,}/g, '')               // --- horizontal rules
    .replace(/\s+/g, ' ')               // collapse whitespace
    .trim();

  return (
    <Card
      className={cn(
        'transition-all border-l-4 group',
        tierColors.border,
        isSelected && 'ring-2 ring-primary/20',
        userAction === 'Accepted' && 'bg-green-50/50',
        userAction === 'Modified' && 'bg-blue-50/50',
        userAction === 'Rejected' && 'bg-red-50/50'
      )}
    >
      {/* Card Header - Always visible */}
      <CardHeader
        className="cursor-pointer py-4 select-none"
        onClick={handleHeaderClick}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            {/* Selection checkbox - visible on hover */}
            <div className="checkbox-area pt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={handleCheckboxChange}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
            </div>

            {/* Question index — bold, readable */}
            <span className="text-sm font-bold font-mono text-foreground bg-muted px-2.5 py-1 rounded shrink-0">
              Q{question.index + 1}
            </span>

            {/* Question + response preview */}
            <div className="flex-1 min-w-0">
              {/* Question text — strong contrast, clear hierarchy */}
              <p className="text-sm font-semibold text-foreground leading-relaxed">
                {question.incoming_question}
              </p>

              {/* Response preview when collapsed — visually separated */}
              {!isExpanded && responseText && (
                <div className="mt-2 pt-2 border-t border-border/50">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Response
                  </span>
                  <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2 leading-relaxed">
                    {previewText}
                  </p>
                  <span className="text-xs text-primary/70 hover:text-primary mt-1 inline-block">
                    Click to expand
                  </span>
                </div>
              )}

              {/* NoMatch hint when collapsed and no response */}
              {!isExpanded && !responseText && question.response_tier === 'NoMatch' && userAction === 'Pending' && (
                <div className="mt-2 pt-2 border-t border-dashed border-tier-nomatch/30">
                  <p className="text-sm text-tier-nomatch">
                    No match found — click to provide an answer
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Badges and expand icon */}
          <div className="flex items-center gap-2 shrink-0">
            {userAction !== 'Pending' && (
              <span
                className={cn(
                  'text-xs font-medium px-2 py-0.5 rounded',
                  userAction === 'Accepted' && 'bg-green-100 text-green-700',
                  userAction === 'Modified' && 'bg-blue-100 text-blue-700',
                  userAction === 'Rejected' && 'bg-red-100 text-red-700'
                )}
              >
                {userAction}
              </span>
            )}
            <TierBadge tier={question.response_tier} size="sm" />
            <SourceBadge source={question.answer_source} size="sm" />
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </div>
        </div>
      </CardHeader>

      {/* Card Content - Expandable */}
      {isExpanded && (
        <CardContent className="pt-0 space-y-4 max-w-3xl">
          {/* Response section */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Response
            </label>
            {isEditing ? (
              <ResponseEditor
                value={editingResponse}
                originalResponse={question.suggested_response}
                onChange={onUpdateEditingResponse}
                onSave={onSaveEdit}
                onCancel={onCancelEdit}
              />
            ) : (
              <div className="bg-muted/50 rounded-lg p-4 text-sm leading-relaxed prose prose-sm prose-neutral dark:prose-invert prose-table:border-collapse prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-1.5 prose-th:border prose-th:border-border prose-th:px-3 prose-th:py-1.5 prose-th:bg-muted">
                <Markdown remarkPlugins={[remarkGfm]}>{responseText}</Markdown>
              </div>
            )}
          </div>

          {/* Citations */}
          {!isEditing && question.citations.length > 0 && (
            <CitationPanel citations={question.citations} />
          )}

          {/* Action buttons - only show when not editing */}
          {!isEditing && (
            <div className="pt-2 border-t">
              <ActionButtons
                onAccept={onAccept}
                onModify={onStartEdit}
                onReject={onReject}
                currentAction={userAction}
              />
            </div>
          )}

          {/* Vault capture prompt - shown after accepting non-vault answers */}
          {!isEditing && showVaultCapture && currentSessionId && (
            <VaultCapturePrompt
              questionText={question.incoming_question}
              answerText={question.userResponse || question.suggested_response}
              category={question.category}
              sessionId={currentSessionId}
              responseId={question.question_id}
              onSave={handleVaultSave}
              onSkip={handleVaultSkip}
            />
          )}
        </CardContent>
      )}
    </Card>
  );
}
